/* eslint-disable no-loop-func */
/* eslint-disable no-plusplus */
import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

import { useModal } from '~/shared/hooks/useModal';

import {
  ActionButtonTagEnum,
  useOrderActionButtons,
} from './useOrderActionButtons';
import { useKeyUp } from './useKeyUp';
import { useTickets } from './tickets';
import { useHeader } from './header';
import { useDisplay } from './display';
import { useConsolidate } from './consolidate';
import { useConfirmModal } from './confirmModal';
import { useActions } from './actions';
import { IDisplayTicketItem } from '../interfaces/IDisplayTicket';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';
import { KeyFocusEnum } from '../enums/KeyFocusEnum';
import { KeyActionsTagEnum } from '../enums/KeyActionsTagEnum';
import { HeaderOptionsEnum } from '../enums/HeaderOptionsEnum';
import { TicketObservationModal } from '../components/TicketObservationModel';
import { useAuth } from '../../auth/hooks/auth';

enum KeyActionsEnum {
  'top' = 'top',
  'bottom' = 'bottom',
  'left' = 'left',
  'right' = 'right',
  'return' = 'return',
  'confirm' = 'confirm',
}

interface IOrderPositions {
  leftPosition: number;
  topPosition: number;
  bottomPosition: number;
  rightPosition: number;
  width: number;
  height: number;
  offsetLeftPosition: number;
  offsetTopPosition: number;
}

interface IOrderFocus {
  index: number;
  inside: boolean;
  orderId?: string;
  orderTotalItems?: IDisplayTicketItem[];
  orderItems?: IDisplayTicketItem[];
  orderStatus?: OrderStatusEnum;
  orderIsCanceled?: boolean;
}

interface IFocusInOrder {
  type: 'buttons' | 'items' | 'header';
  value: number;
  tag?: ActionButtonTagEnum;
  itemId?: string;
  remainingOrderIndex?: number;
  remainingOrderItemIndex?: number;
}

interface IKeyActionsContextData {
  orderFocus: IOrderFocus;
  onArrowBottom(index: number): void;
  onArrowTop(index: number): void;
  headerFocus: HeaderOptionsEnum;
  keyFocus: KeyFocusEnum;
  changeKeyFocus: (newKeyFocus: KeyFocusEnum) => void;
  focusInOrder: IFocusInOrder;
  consolidateItemFocus: number;
  confirmModalFocus: 'cancel' | 'confirm';
  displaySelectionFocus: number;
}

const KeyActionsContext = createContext({} as IKeyActionsContextData);

const KeyActionsProvider: FC = ({ children }) => {
  const { statusJourney } = useDisplay();
  const {
    selectedInHeader,
    oldSelectedInHeader,
    getPreviousStatusToReturn,
    changeSelectedInHeader,
  } = useHeader();
  const [isObservationModalOpen, setIsObservationModalOpen] =
    useState<boolean>(false);
  const { openModal, closeModal } = useModal();
  const { tickets, loadTickets } = useTickets();
  const { consolidatedItems } = useConsolidate();
  const { actionButtons, getActionButtonsForSelectedStatus } =
    useOrderActionButtons();
  const { openConfirmModal, closeConfirmModal, callbackFn } = useConfirmModal();
  const {
    handleChangeTicketStatus,
    handleChangeTicketItemStatus,
    handleChangeTicketSubItemStatus,
  } = useActions();
  const { configuration } = useAuth();

  const [headerFocus, setHeaderFocus] = useState<HeaderOptionsEnum>(
    HeaderOptionsEnum.open
  );
  const [orderFocus, setOrderFocus] = useState<IOrderFocus>({
    index: 0,
    inside: false,
  });

  const actionButtonsForSelectedStatus = getActionButtonsForSelectedStatus(
    selectedInHeader as any
  ).reverse();

  const [focusInOrder, setFocusInOrder] = useState<IFocusInOrder>({
    type: 'buttons',
    value: 0,
    tag: actionButtonsForSelectedStatus[0]?.tag,
  });
  const [keyFocus, setKeyFocus] = useState<KeyFocusEnum>(KeyFocusEnum.header);
  const [consolidateItemFocus, setConsolidateItemFocus] = useState<number>(0);
  const [confirmModalFocus, setConfirmModalFocus] = useState<
    'cancel' | 'confirm'
  >('confirm');
  const [displaySelectionFocus, setDisplaySelectionFocus] = useState<number>(0);

  const displayIncludesStatus = useCallback(
    (status: OrderStatusEnum) => {
      if (!configuration) return false;
      if (!statusJourney || !statusJourney.length) return false;
      return statusJourney.includes(status);
    },
    [configuration, statusJourney]
  );

  const getPositions = (id: string): IOrderPositions => {
    const element = document.getElementById(id);

    if (element) {
      const { left, top, bottom, right, width, height } =
        element.getBoundingClientRect();

      const { offsetLeft, offsetTop } = element;

      return {
        leftPosition: left,
        topPosition: top,
        bottomPosition: bottom,
        rightPosition: right,
        width,
        height,
        offsetLeftPosition: offsetLeft,
        offsetTopPosition: offsetTop,
      };
    }

    return {
      leftPosition: 0,
      topPosition: 0,
      bottomPosition: 0,
      rightPosition: 0,
      width: 0,
      height: 0,
      offsetLeftPosition: 0,
      offsetTopPosition: 0,
    };
  };

  const changeKeyFocus = useCallback((newKeyFocus: KeyFocusEnum) => {
    setKeyFocus(newKeyFocus);
  }, []);

  const orderActions = useCallback(
    (action: KeyActionsEnum): boolean => {
      if (keyFocus === KeyFocusEnum['orders-list'] && !orderFocus.inside) {
        if (action === KeyActionsEnum.top) {
          const headerElement = document.getElementById('header');

          if (headerElement) {
            const { bottom: headerBottomPosition } =
              headerElement?.getBoundingClientRect();

            if (headerBottomPosition) {
              const { topPosition: orderFocusTopPosition } = getPositions(
                `order-index-${orderFocus.index}`
              );

              if (headerBottomPosition + 10 === orderFocusTopPosition) {
                setKeyFocus(KeyFocusEnum.header);
              } else if (orderFocus.index > 0) {
                setOrderFocus((prevState) => ({
                  ...prevState,
                  index: prevState.index - 1,
                }));
              }
            }
          }
        }

        if (action === KeyActionsEnum.bottom) {
          if (orderFocus.index < tickets?.length - 1) {
            setOrderFocus((prevState) => ({
              ...prevState,
              index: prevState.index + 1,
            }));
          }
        }

        if (action === KeyActionsEnum.confirm) {
          setFocusInOrder({
            type: 'buttons',
            value: 0,
            tag: actionButtonsForSelectedStatus[0].tag,
          });

          const mainTickets = tickets.filter((tkt) => !tkt.continuationOf);
          const ticket = mainTickets[orderFocus.index];
          ticket.items = (ticket.items || []).map((item) => ({
            ...item,
            subitems: item.catalogLine ? [] : item.subitems,
          }));

          const orderTotalItems: IDisplayTicketItem[] = [];
          if ((ticket.items || []).length) {
            for (const item of ticket.items) {
              orderTotalItems.push(item);
              if ((item.subitems || []).length && !item.catalogLine) {
                for (const sub of item.subitems) {
                  orderTotalItems.push({
                    ...sub,
                    comboItemOf: item.id,
                  } as IDisplayTicketItem);
                }
              }
            }
          }

          const continuation =
            tickets.filter((tkt) => tkt.continuationOf === ticket.id) || [];

          if (continuation.length) {
            for (const continuationTicket of continuation) {
              const { items: continuationItens } = continuationTicket;
              if ((continuationItens || []).length) {
                for (const continuationItem of continuationItens) {
                  orderTotalItems.push(continuationItem);
                  if (continuationItem.subitems?.length) {
                    for (const sub of continuationItem.subitems) {
                      orderTotalItems.push({
                        ...sub,
                        comboItemOf: continuationItem.id,
                      } as IDisplayTicketItem);
                    }
                  }
                }
              }
            }
          }

          setOrderFocus((prevState) => ({
            ...prevState,
            inside: true,
            orderId: ticket?.id,
            orderTotalItems,
            orderItems: ticket?.items,
            orderStatus: ticket?.status,
            orderIsCanceled: ticket?.status === 'canceled',
          }));
        }

        return true;
      }

      return false;
    },
    [actionButtonsForSelectedStatus, keyFocus, orderFocus, tickets]
  );

  const inOrderActions = useCallback(
    (action: KeyActionsEnum): boolean => {
      if (keyFocus === KeyFocusEnum['orders-list'] && orderFocus.inside) {
        if (action === KeyActionsEnum.top) {
          if (focusInOrder.type === 'buttons') {
            setFocusInOrder({
              value: 0,
              type: 'header',
            });
          } else if (focusInOrder.type === 'items') {
            if (focusInOrder.value === 0) {
              setFocusInOrder({
                value: 0,
                type: 'buttons',
                tag: actionButtonsForSelectedStatus[0]?.tag,
              });
            } else {
              setFocusInOrder((prevState) => {
                if (
                  typeof prevState.remainingOrderIndex === 'number' &&
                  typeof prevState.remainingOrderItemIndex === 'number'
                ) {
                  if (prevState.remainingOrderItemIndex === 0) {
                    const prevOrderItemsListElement = document.getElementById(
                      `items-list-index-${orderFocus.index}-remaining-index-${
                        prevState.remainingOrderIndex - 1
                      }`
                    );

                    return {
                      ...prevState,
                      value: prevState.value - 1,
                      itemId: orderFocus.orderTotalItems
                        ? orderFocus.orderTotalItems[prevState.value - 1].id
                        : '',
                      remainingOrderIndex: prevState.remainingOrderIndex - 1,
                      remainingOrderItemIndex:
                        prevOrderItemsListElement?.childElementCount
                          ? prevOrderItemsListElement.childElementCount - 1
                          : 0,
                    };
                  }

                  return {
                    ...prevState,
                    value: prevState.value - 1,
                    itemId: orderFocus.orderTotalItems
                      ? orderFocus.orderTotalItems[prevState.value - 1].id
                      : '',
                    remainingOrderItemIndex:
                      prevState.remainingOrderItemIndex - 1,
                  };
                }

                return {
                  ...prevState,
                  value: prevState.value - 1,
                  itemId: orderFocus.orderTotalItems
                    ? orderFocus.orderTotalItems[prevState.value - 1].id
                    : '',
                };
              });
            }
          }
        }

        if (action === KeyActionsEnum.bottom) {
          if (focusInOrder.type === 'header') {
            setFocusInOrder({
              value: 0,
              type: 'buttons',
              tag: actionButtonsForSelectedStatus[0]?.tag,
            });
          } else {
            if (focusInOrder.type === 'buttons') {
              setFocusInOrder({
                value: 0,
                type: 'items',
                itemId: orderFocus.orderTotalItems
                  ? orderFocus.orderTotalItems[0].id
                  : '',
              });
            }

            if (focusInOrder.type === 'items') {
              if (
                focusInOrder.value !==
                (orderFocus.orderTotalItems &&
                  orderFocus.orderTotalItems?.length - 1)
              ) {
                setFocusInOrder((prevState) => {
                  if (
                    typeof prevState.remainingOrderIndex === 'number' &&
                    typeof prevState.remainingOrderItemIndex === 'number'
                  ) {
                    const orderItemElement = document.getElementById(
                      `order-index-${orderFocus.index}-remaining-index-${
                        prevState.remainingOrderIndex
                      }-item-index-${prevState.remainingOrderItemIndex + 1}`
                    );

                    if (!orderItemElement) {
                      return {
                        ...prevState,
                        value: prevState.value + 1,
                        itemId: orderFocus.orderTotalItems
                          ? orderFocus.orderTotalItems[prevState.value + 1].id
                          : '',
                        remainingOrderIndex: prevState.remainingOrderIndex + 1,
                        remainingOrderItemIndex: 0,
                      };
                    }

                    return {
                      ...prevState,
                      value: prevState.value + 1,
                      itemId: orderFocus.orderTotalItems
                        ? orderFocus.orderTotalItems[prevState.value + 1].id
                        : '',
                      remainingOrderItemIndex:
                        prevState.remainingOrderItemIndex + 1,
                    };
                  }

                  const orderItemElement = document.getElementById(
                    `order-index-${orderFocus.index}-item-index-${
                      prevState.value + 1
                    }`
                  );

                  if (!orderItemElement) {
                    return {
                      ...prevState,
                      value: prevState.value + 1,
                      itemId: orderFocus.orderTotalItems
                        ? orderFocus.orderTotalItems[prevState.value + 1].id
                        : '',
                      remainingOrderIndex: 0,
                      remainingOrderItemIndex: 0,
                    };
                  }

                  return {
                    ...prevState,
                    value: prevState.value + 1,
                    itemId: orderFocus.orderTotalItems
                      ? orderFocus.orderTotalItems[prevState.value + 1].id
                      : '',
                  };
                });
              }
            }
          }
        }

        if (action === KeyActionsEnum.right) {
          if (
            focusInOrder.value < actionButtonsForSelectedStatus.length - 1 &&
            focusInOrder.type === 'buttons'
          ) {
            setFocusInOrder((prevState) => ({
              ...prevState,
              value: prevState.value + 1,
              tag: actionButtonsForSelectedStatus[prevState.value + 1].tag,
            }));
          }
        }

        if (action === KeyActionsEnum.left) {
          if (focusInOrder.value > 0 && focusInOrder.type === 'buttons') {
            setFocusInOrder((prevState) => ({
              ...prevState,
              value: prevState.value - 1,
              tag: actionButtonsForSelectedStatus[prevState.value - 1].tag,
            }));
          }
        }

        if (action === KeyActionsEnum.return) {
          setOrderFocus((prevState) => ({
            ...prevState,
            inside: false,
          }));
        }

        if (action === KeyActionsEnum.confirm) {
          if (focusInOrder.type === 'buttons') {
            if (focusInOrder.tag) {
              const findActionButton = actionButtonsForSelectedStatus.find(
                (btn) => btn.tag === focusInOrder.tag
              );

              let actionButton = findActionButton;

              if (
                actionButton &&
                actionButton.tag === ActionButtonTagEnum.done &&
                orderFocus.orderStatus === OrderStatusEnum.open &&
                displayIncludesStatus(OrderStatusEnum['in-progress'])
              ) {
                const findInPreparedActionButton = actionButtons.find(
                  (btn) => btn.tag === ActionButtonTagEnum.start
                );

                actionButton = findInPreparedActionButton;
              }

              if (actionButton) {
                let callbackFunction: any;

                if (actionButton.tag === ActionButtonTagEnum.start) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId || '',
                      OrderStatusEnum['in-progress']
                    );
                }

                if (actionButton.tag === ActionButtonTagEnum.wait) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId || '',
                      OrderStatusEnum.waiting
                    );
                }

                if (actionButton.tag === ActionButtonTagEnum.reopen) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId || '',
                      getPreviousStatusToReturn()
                    );
                }

                if (actionButton.tag === ActionButtonTagEnum.deliver) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId || '',
                      OrderStatusEnum.delivered
                    );
                }

                if (actionButton.tag === ActionButtonTagEnum.done) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId || '',
                      OrderStatusEnum.done
                    );
                }

                if (orderFocus.orderIsCanceled) {
                  callbackFunction = () =>
                    handleChangeTicketStatus(
                      orderFocus.orderId,
                      OrderStatusEnum.canceled
                    );
                }

                const canceledModalText =
                  'Deseja realmente remover esse pedido dessa tela';

                setConfirmModalFocus('confirm');

                if (
                  configuration?.settings?.displayConfirmationMessage ||
                  actionButton.tag === ActionButtonTagEnum.reopen
                ) {
                  openConfirmModal(
                    orderFocus.orderIsCanceled
                      ? canceledModalText
                      : actionButton.confirmModalTitle,
                    callbackFunction
                  );
                  setKeyFocus(KeyFocusEnum['confirm-modal']);
                } else {
                  callbackFunction();

                  setKeyFocus(KeyFocusEnum['orders-list']);

                  const findOrderIndex = tickets.findIndex(
                    (order) => order.id === orderFocus.orderId
                  );

                  if (
                    tickets.length === 1 &&
                    actionButton.tag !== ActionButtonTagEnum.start
                  ) {
                    setKeyFocus(KeyFocusEnum.header);
                  }

                  setOrderFocus((prevState) => ({
                    ...prevState,
                    ...(findOrderIndex === tickets.length - 1 && {
                      index:
                        prevState.index === 0 ||
                        actionButton?.tag === ActionButtonTagEnum.start
                          ? prevState.index
                          : prevState.index - 1,
                    }),
                    inside: false,
                    ...(actionButton?.tag === ActionButtonTagEnum.start && {
                      orderStatus: OrderStatusEnum['in-progress'],
                    }),
                  }));
                }
              }
            }
          }

          if (focusInOrder.type === 'items') {
            setConfirmModalFocus('confirm');
            if (orderFocus.orderId && focusInOrder.itemId) {
              const findItem = orderFocus.orderTotalItems?.find(
                (item) => item.id === focusInOrder.itemId
              );

              const withConfirmation =
                configuration.settings.displayConfirmationMessage;

              const statusToSet =
                findItem?.status === OrderStatusEnum.done
                  ? OrderStatusEnum.open
                  : OrderStatusEnum.done;

              const afterStatusChange = (): void => {
                setOrderFocus((prevState) => ({
                  ...prevState,
                  orderTotalItems: prevState.orderTotalItems?.map((item) => ({
                    ...item,
                    ...(item.id === findItem.id && {
                      status: statusToSet,
                    }),
                  })),
                }));
              };

              if (withConfirmation || statusToSet === OrderStatusEnum.open) {
                setKeyFocus(KeyFocusEnum['confirm-modal']);
                openConfirmModal(
                  `Deseja realmente marcar esse item como ${
                    findItem?.status === OrderStatusEnum.done
                      ? 'aberto'
                      : 'pronto'
                  }?`,
                  () => {
                    findItem?.comboItemOf
                      ? handleChangeTicketSubItemStatus(
                          findItem.comboItemOf,
                          statusToSet,
                          findItem.id,
                          orderFocus.orderId
                        ).then(afterStatusChange)
                      : handleChangeTicketItemStatus(
                          orderFocus.orderId as string,
                          focusInOrder.itemId as string,
                          statusToSet
                        ).then(afterStatusChange);

                    setKeyFocus(KeyFocusEnum['confirm-modal']);
                  }
                );
              } else {
                // eslint-disable-next-line no-lonely-if
                if (findItem?.comboItemOf) {
                  handleChangeTicketSubItemStatus(
                    findItem.comboItemOf,
                    statusToSet,
                    findItem.id,
                    orderFocus.orderId
                  ).then(afterStatusChange);
                } else {
                  handleChangeTicketItemStatus(
                    orderFocus.orderId as string,
                    focusInOrder.itemId as string,
                    statusToSet
                  ).then(afterStatusChange);
                }
              }
            }
          }
        }

        return true;
      }

      return false;
    },
    [
      keyFocus,
      orderFocus,
      focusInOrder,
      actionButtonsForSelectedStatus,
      displayIncludesStatus,
      actionButtons,
      configuration,
      handleChangeTicketStatus,
      openConfirmModal,
      getPreviousStatusToReturn,
      tickets,
      handleChangeTicketSubItemStatus,
      handleChangeTicketItemStatus,
    ]
  );

  const headerActions = useCallback(
    (action: KeyActionsEnum): boolean => {
      if (keyFocus === KeyFocusEnum.header) {
        if (action === KeyActionsEnum.bottom) {
          if (tickets.length > 0) {
            if (selectedInHeader === HeaderOptionsEnum.consolidate) {
              setKeyFocus(KeyFocusEnum['consolidate-list']);
            } else {
              setKeyFocus(KeyFocusEnum['orders-list']);
            }
          }
        }

        if (action === KeyActionsEnum.left) {
          if (headerFocus === HeaderOptionsEnum.consolidate) {
            setHeaderFocus(HeaderOptionsEnum.canceled);
          }

          if (headerFocus === HeaderOptionsEnum.canceled) {
            if (displayIncludesStatus(OrderStatusEnum.delivered)) {
              setHeaderFocus(HeaderOptionsEnum.delivered);
            } else {
              setHeaderFocus(HeaderOptionsEnum.done);
            }
          }

          if (headerFocus === HeaderOptionsEnum.delivered) {
            setHeaderFocus(HeaderOptionsEnum.done);
          }

          if (headerFocus === HeaderOptionsEnum.done) {
            if (displayIncludesStatus(OrderStatusEnum.waiting)) {
              setHeaderFocus(HeaderOptionsEnum.waiting);
            } else if (displayIncludesStatus(OrderStatusEnum['in-progress'])) {
              setHeaderFocus(HeaderOptionsEnum['in-progress']);
            } else {
              setHeaderFocus(HeaderOptionsEnum.open);
            }
          }

          if (headerFocus === HeaderOptionsEnum.waiting) {
            if (displayIncludesStatus(OrderStatusEnum['in-progress'])) {
              setHeaderFocus(HeaderOptionsEnum['in-progress']);
            } else {
              setHeaderFocus(HeaderOptionsEnum.open);
            }
          }

          if (headerFocus === HeaderOptionsEnum['in-progress']) {
            setHeaderFocus(HeaderOptionsEnum.open);
          }

          if (headerFocus === HeaderOptionsEnum.open) {
            setHeaderFocus(HeaderOptionsEnum.more);
          }
        }

        if (action === KeyActionsEnum.right) {
          if (headerFocus === HeaderOptionsEnum.more) {
            setHeaderFocus(HeaderOptionsEnum.open);
          }

          if (headerFocus === HeaderOptionsEnum.open) {
            if (displayIncludesStatus(OrderStatusEnum['in-progress'])) {
              setHeaderFocus(HeaderOptionsEnum['in-progress']);
            } else if (displayIncludesStatus(OrderStatusEnum.waiting)) {
              setHeaderFocus(HeaderOptionsEnum.waiting);
            } else {
              setHeaderFocus(HeaderOptionsEnum.done);
            }
          }

          if (headerFocus === HeaderOptionsEnum['in-progress']) {
            if (displayIncludesStatus(OrderStatusEnum.waiting)) {
              setHeaderFocus(HeaderOptionsEnum.waiting);
            } else {
              setHeaderFocus(HeaderOptionsEnum.done);
            }
          }

          if (headerFocus === HeaderOptionsEnum.waiting) {
            setHeaderFocus(HeaderOptionsEnum.done);
          }

          if (headerFocus === HeaderOptionsEnum.done) {
            if (displayIncludesStatus(OrderStatusEnum.delivered)) {
              setHeaderFocus(HeaderOptionsEnum.delivered);
            } else {
              setHeaderFocus(HeaderOptionsEnum.canceled);
            }
          }

          if (headerFocus === HeaderOptionsEnum.delivered) {
            setHeaderFocus(HeaderOptionsEnum.canceled);
          }

          if (headerFocus === HeaderOptionsEnum.canceled) {
            setHeaderFocus(HeaderOptionsEnum.consolidate);
          }
        }

        if (action === KeyActionsEnum.confirm) {
          if (orderFocus.index !== 0) {
            setOrderFocus((prevState) => ({
              ...prevState,
              index: 0,
            }));
          }

          changeSelectedInHeader(headerFocus, loadTickets);
        }

        if (action === KeyActionsEnum.return) {
          if (selectedInHeader === HeaderOptionsEnum.consolidate) {
            setHeaderFocus(oldSelectedInHeader);
            changeSelectedInHeader(oldSelectedInHeader);
          }
        }

        return true;
      }
      return false;
    },
    [
      keyFocus,
      tickets.length,
      selectedInHeader,
      headerFocus,
      displayIncludesStatus,
      orderFocus.index,
      changeSelectedInHeader,
      loadTickets,
      oldSelectedInHeader,
    ]
  );

  const consolidateItemActions = useCallback(
    (action: KeyActionsEnum) => {
      if (keyFocus === KeyFocusEnum['consolidate-list']) {
        if (action === KeyActionsEnum.top) {
          if (consolidateItemFocus > 0) {
            setConsolidateItemFocus((prevState) => prevState - 1);
          } else {
            setKeyFocus(KeyFocusEnum.header);
          }
        }

        if (action === KeyActionsEnum.bottom) {
          if (consolidateItemFocus < consolidatedItems.length - 1) {
            setConsolidateItemFocus((prevState) => prevState + 1);
          }
        }

        return true;
      }

      return false;
    },
    [consolidateItemFocus, consolidatedItems.length, keyFocus]
  );

  const confirmModalActions = useCallback(
    (action: KeyActionsEnum): boolean => {
      if (keyFocus === KeyFocusEnum['confirm-modal']) {
        if (action === KeyActionsEnum.left) {
          if (confirmModalFocus === 'confirm') {
            setConfirmModalFocus('cancel');
          }
        }

        if (action === KeyActionsEnum.right) {
          if (confirmModalFocus === 'cancel') {
            setConfirmModalFocus('confirm');
          }
        }

        if (action === KeyActionsEnum.confirm) {
          if (confirmModalFocus === 'confirm') {
            if (orderFocus.orderId) {
              callbackFn?.();

              closeConfirmModal();
              setKeyFocus(KeyFocusEnum['orders-list']);

              const findOrderIndex = tickets.findIndex(
                (order) => order.id === orderFocus.orderId
              );

              const newStatusIsInProgress = String(callbackFn).includes(
                OrderStatusEnum['in-progress']
              );

              if (tickets.length === 1 && !newStatusIsInProgress) {
                setKeyFocus(KeyFocusEnum.header);
              }

              setOrderFocus((prevState) => ({
                ...prevState,
                ...(findOrderIndex === tickets.length - 1 && {
                  index:
                    prevState.index === 0 || newStatusIsInProgress
                      ? prevState.index
                      : prevState.index - 1,
                }),
                inside: false,
              }));
            }
          }

          if (confirmModalFocus === 'cancel') {
            closeConfirmModal();

            setKeyFocus(KeyFocusEnum['orders-list']);
          }
        }

        if (action === KeyActionsEnum.return) {
          setKeyFocus(KeyFocusEnum['orders-list']);
          closeConfirmModal();
        }

        return true;
      }

      return false;
    },
    [
      callbackFn,
      closeConfirmModal,
      confirmModalFocus,
      keyFocus,
      orderFocus.orderId,
      tickets,
    ]
  );

  const displaySelectionActions = useCallback(
    (action: KeyActionsEnum): boolean => {
      if (keyFocus === KeyFocusEnum['display-selection']) {
        if (action === KeyActionsEnum.top) {
          setDisplaySelectionFocus((prevState) => {
            if (prevState === 0) return prevState;

            return prevState - 1;
          });
        }

        if (action === KeyActionsEnum.bottom) {
          setDisplaySelectionFocus((prevState) => {
            if (configuration?.settings?.displays?.length - 1 <= prevState) {
              return prevState;
            }

            return prevState + 1;
          });
        }

        if (action === KeyActionsEnum.confirm) {
          const displayOptionElement = document.getElementById(
            `display-option-index-${displaySelectionFocus}`
          );

          if (displayOptionElement) displayOptionElement.click();
        }

        return true;
      }
    },
    [keyFocus, configuration?.settings?.displays?.length, displaySelectionFocus]
  );

  const onArrowTop = useCallback(() => {
    if (inOrderActions(KeyActionsEnum.top)) return;

    if (headerActions(KeyActionsEnum.top)) return;

    if (consolidateItemActions(KeyActionsEnum.top)) return;

    if (displaySelectionActions(KeyActionsEnum.top)) return;

    orderActions(KeyActionsEnum.top);

    if (orderFocus && orderFocus.index === 0) {
      setKeyFocus(KeyFocusEnum.header);
    }
  }, [
    consolidateItemActions,
    displaySelectionActions,
    headerActions,
    inOrderActions,
    orderActions,
    orderFocus,
  ]);

  const onArrowBottom = useCallback(() => {
    if (inOrderActions(KeyActionsEnum.bottom)) return;

    if (headerActions(KeyActionsEnum.bottom)) return;

    if (consolidateItemActions(KeyActionsEnum.bottom)) return;

    if (displaySelectionActions(KeyActionsEnum.bottom)) return;

    orderActions(KeyActionsEnum.bottom);
  }, [
    consolidateItemActions,
    headerActions,
    displaySelectionActions,
    inOrderActions,
    orderActions,
  ]);

  const onArrowLeft = useCallback(() => {
    if (inOrderActions(KeyActionsEnum.left)) return;

    if (headerActions(KeyActionsEnum.left)) return;

    if (confirmModalActions(KeyActionsEnum.left)) return;

    const {
      leftPosition: orderFocusLeftPosition,
      topPosition: orderFocusTopPosition,
      bottomPosition: orderFocusBottomPosition,
    } = getPositions(`order-index-${orderFocus.index}`);

    if (orderFocusLeftPosition && orderFocusTopPosition) {
      let newIndex = 0;

      for (let { index } = orderFocus; index >= 0; index--) {
        const {
          leftPosition: orderLeftPosition,
          topPosition: orderTopPosition,
          bottomPosition: orderBottomPosition,
        } = getPositions(`order-index-${index}`);

        if (orderLeftPosition < orderFocusLeftPosition) {
          if (
            orderFocusTopPosition === orderTopPosition ||
            orderBottomPosition < orderFocusBottomPosition
          ) {
            setOrderFocus((prevState) => ({
              ...prevState,
              index,
            }));

            return;
          }

          if (orderBottomPosition < orderFocusBottomPosition) {
            setOrderFocus((prevState) => ({
              ...prevState,
              index: newIndex,
            }));

            return;
          }

          if (orderBottomPosition > orderFocusBottomPosition) {
            if (index === 0) {
              setOrderFocus((prevState) => ({
                ...prevState,
                index: 0,
              }));
            }

            newIndex = index;
          }
        }
      }
    }
  }, [confirmModalActions, headerActions, inOrderActions, orderFocus]);

  const onArrowRight = useCallback(() => {
    if (inOrderActions(KeyActionsEnum.right)) return;

    if (headerActions(KeyActionsEnum.right)) return;

    if (confirmModalActions(KeyActionsEnum.right)) return;

    const {
      leftPosition: orderFocusLeftPosition,
      topPosition: orderFocusTopPosition,
    } = getPositions(`order-index-${orderFocus.index}`);

    if (orderFocusLeftPosition && orderFocusTopPosition) {
      for (let { index } = orderFocus; index < tickets?.length; index++) {
        const {
          leftPosition: orderLeftPosition,
          topPosition: orderTopPosition,
          bottomPosition: orderBottomPosition,
        } = getPositions(`order-index-${index}`);

        if (
          orderLeftPosition > orderFocusLeftPosition &&
          (orderFocusTopPosition === orderTopPosition ||
            orderBottomPosition > orderFocusTopPosition)
        ) {
          setOrderFocus((prevState) => ({
            ...prevState,
            index,
          }));

          return;
        }
      }
    }
  }, [confirmModalActions, headerActions, inOrderActions, orderFocus, tickets]);

  const onConfirm = useCallback(() => {
    if (
      keyFocus === KeyFocusEnum['orders-list'] &&
      focusInOrder.type === 'header'
    ) {
      const ticket = tickets.find((where) => where.id === orderFocus.orderId);
      if (ticket && ticket.observations?.length > 100) {
        if (isObservationModalOpen) {
          closeModal();
          setIsObservationModalOpen(false);
        } else {
          setIsObservationModalOpen(true);
          openModal(TicketObservationModal, {
            modalProps: {
              title: `${ticket.code} - Observação`,
            },
            props: {
              observation: ticket.observations,
            },
          });
        }

        return;
      }
    }

    if (inOrderActions(KeyActionsEnum.confirm)) return;

    if (orderActions(KeyActionsEnum.confirm)) return;

    if (confirmModalActions(KeyActionsEnum.confirm)) return;

    if (displaySelectionActions(KeyActionsEnum.confirm)) return;

    headerActions(KeyActionsEnum.confirm);
  }, [
    keyFocus,
    focusInOrder,
    displaySelectionActions,
    inOrderActions,
    orderActions,
    confirmModalActions,
    headerActions,
    tickets,
    orderFocus,
    isObservationModalOpen,
    closeModal,
    openModal,
  ]);

  const onReturn = useCallback(() => {
    setFocusInOrder({
      type: 'buttons',
      value: 0,
      tag: actionButtonsForSelectedStatus[0]?.tag,
    });

    if (inOrderActions(KeyActionsEnum.return)) return;

    if (headerActions(KeyActionsEnum.return)) return;

    confirmModalActions(KeyActionsEnum.return);
  }, [
    actionButtonsForSelectedStatus,
    confirmModalActions,
    headerActions,
    inOrderActions,
  ]);

  const onConsolidate = useCallback(() => {
    setKeyFocus(KeyFocusEnum.header);
    setHeaderFocus(HeaderOptionsEnum.consolidate);
    changeSelectedInHeader(HeaderOptionsEnum.consolidate, loadTickets);
  }, [changeSelectedInHeader, loadTickets]);

  const onGoToOpen = useCallback(() => {
    if (displayIncludesStatus(OrderStatusEnum.open)) {
      setKeyFocus(KeyFocusEnum.header);
      setHeaderFocus(HeaderOptionsEnum.open);
      changeSelectedInHeader(HeaderOptionsEnum.open, loadTickets);
    }
  }, [changeSelectedInHeader, displayIncludesStatus, loadTickets]);

  const onGoToInProgress = useCallback(() => {
    if (displayIncludesStatus(OrderStatusEnum['in-progress'])) {
      setKeyFocus(KeyFocusEnum.header);
      setHeaderFocus(HeaderOptionsEnum['in-progress']);
      changeSelectedInHeader(HeaderOptionsEnum['in-progress'], loadTickets);
    }
  }, [changeSelectedInHeader, displayIncludesStatus, loadTickets]);

  const onGoToWaiting = useCallback(() => {
    if (displayIncludesStatus(OrderStatusEnum.waiting)) {
      setKeyFocus(KeyFocusEnum.header);
      setHeaderFocus(HeaderOptionsEnum.waiting);
      changeSelectedInHeader(HeaderOptionsEnum.waiting, loadTickets);
    }
  }, [changeSelectedInHeader, displayIncludesStatus, loadTickets]);

  const onGoToDone = useCallback(() => {
    if (displayIncludesStatus(OrderStatusEnum.done)) {
      setKeyFocus(KeyFocusEnum.header);
      setHeaderFocus(HeaderOptionsEnum.done);
      changeSelectedInHeader(HeaderOptionsEnum.done, loadTickets);
    }
  }, [changeSelectedInHeader, displayIncludesStatus, loadTickets]);

  const onGoToDelivered = useCallback(() => {
    if (displayIncludesStatus(OrderStatusEnum.delivered)) {
      setKeyFocus(KeyFocusEnum.header);
      setHeaderFocus(HeaderOptionsEnum.delivered);
      changeSelectedInHeader(HeaderOptionsEnum.delivered, loadTickets);
    }
  }, [changeSelectedInHeader, displayIncludesStatus, loadTickets]);

  const onGoToCanceled = useCallback(() => {
    setKeyFocus(KeyFocusEnum.header);
    setHeaderFocus(HeaderOptionsEnum.canceled);
    changeSelectedInHeader(HeaderOptionsEnum.canceled, loadTickets);
  }, [changeSelectedInHeader, loadTickets]);

  useEffect(() => {
    const { leftPosition, offsetLeftPosition, rightPosition, width } =
      getPositions(`order-index-${orderFocus.index}`);

    if (window.screen.width - 200 <= leftPosition) {
      const listElement = document.getElementById('orders-list');

      listElement?.scrollTo({
        left: offsetLeftPosition + width + 15 - window.screen.width,
        behavior: 'smooth',
      });
    }

    if (rightPosition < 200) {
      const listElement = document.getElementById('orders-list');

      listElement?.scrollTo({
        left: offsetLeftPosition - 15,
        behavior: 'smooth',
      });
    }
  }, [orderFocus]);

  useEffect(() => {
    const {
      topPosition: itemTopPosition,
      bottomPosition: itemBottomPosition,
      height: itemHeight,
      offsetTopPosition: itemOffsetTopPosition,
    } = getPositions(`consolidate-item-${consolidateItemFocus}`);

    const {
      bottomPosition: listBottomPosition,
      topPosition: listTopPosition,
      height: listHeight,
    } = getPositions(`consolidate-list`);

    const { height: headerHeight } = getPositions('header');

    const { height: consolidateTableHeadHeight } = getPositions(
      'consolidate-table-head'
    );

    if (itemBottomPosition > listBottomPosition) {
      const consolidateList = document.getElementById('consolidate-list');

      consolidateList?.scrollTo({
        top:
          itemOffsetTopPosition -
          headerHeight +
          itemHeight * 2 -
          listHeight +
          5,
        behavior: 'smooth',
      });
    }

    if (itemTopPosition < listTopPosition + consolidateTableHeadHeight) {
      const consolidateList = document.getElementById('consolidate-list');

      consolidateList?.scrollTo({
        top: itemOffsetTopPosition - itemHeight,
        behavior: 'smooth',
      });
    }
  }, [consolidateItemFocus]);

  useKeyUp(
    () => onArrowTop(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.top
  );
  useKeyUp(
    () => onArrowBottom(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.bottom
  );
  useKeyUp(
    () => onArrowLeft(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.left
  );
  useKeyUp(
    () => onArrowRight(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.right
  );

  useKeyUp(
    () => onConfirm(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.confirm
  );
  useKeyUp(
    () => onReturn(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.return
  );

  useKeyUp(
    () => onConsolidate(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.consolidate
  );
  useKeyUp(
    () => onGoToOpen(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_open
  );
  useKeyUp(
    () => onGoToInProgress(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_in_progress
  );
  useKeyUp(
    () => onGoToWaiting(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_waiting
  );
  useKeyUp(
    () => onGoToDone(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_done
  );
  useKeyUp(
    () => onGoToDelivered(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_delivered
  );
  useKeyUp(
    () => onGoToCanceled(),
    configuration?.settings?.keyboardActions || [],
    KeyActionsTagEnum.go_to_canceled
  );

  useEffect(() => {
    setFocusInOrder({
      type: 'buttons',
      value: 0,
      tag: actionButtonsForSelectedStatus[0]?.tag,
    });
    setOrderFocus({
      index: 0,
      inside: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInHeader]);

  useEffect(() => {
    if (keyFocus === KeyFocusEnum.header) {
      setOrderFocus({
        index: 0,
        inside: false,
      });

      setFocusInOrder({
        type: 'buttons',
        value: 0,
        tag: actionButtonsForSelectedStatus[0]?.tag,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyFocus]);

  return (
    <KeyActionsContext.Provider
      value={{
        orderFocus,
        onArrowBottom,
        onArrowTop,
        headerFocus,
        keyFocus,
        changeKeyFocus,
        focusInOrder,
        consolidateItemFocus,
        confirmModalFocus,
        displaySelectionFocus,
      }}
    >
      {children}
    </KeyActionsContext.Provider>
  );
};

const useKeyActions = (): IKeyActionsContextData => {
  return useContext(KeyActionsContext);
};

export { KeyActionsProvider, useKeyActions };
